import { useState, useEffect, useRef } from "react";
import { useLanguage } from "./LanguageContext";

const API_URL = (process.env.REACT_APP_API_URL || "") + "/api/gallery.php";
const UPLOADS_URL = (process.env.REACT_APP_API_URL || "") + "/uploads/gallery/";

const PHOTOS_PER_PAGE = 12;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function Gallery() {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [photos, setPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // filename pending confirmation

  const fileRef = useRef();
  const touchStartX = useRef(null);
  const lbTouchStartX = useRef(null);

  useEffect(() => {
    fetch(API_URL + "?action=list")
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(photos.length / PHOTOS_PER_PAGE);
  const pagePhotos = photos.slice(
    (currentPage - 1) * PHOTOS_PER_PAGE,
    currentPage * PHOTOS_PER_PAGE
  );

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("photos[]", file));
    fetch(API_URL + "?action=upload", { method: "POST", body: formData })
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data.photos);
        setCurrentPage(1);
        setMobileIndex(0);
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      })
      .catch(() => setUploading(false));
  };

  const handleDelete = (filename) => {
    setConfirmDelete(filename);
  };

  const confirmDeletePhoto = () => {
    const filename = confirmDelete;
    setConfirmDelete(null);
    fetch(API_URL + "?action=delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPhotos(data.photos);
        setLightboxIndex(null);
        setCurrentPage(1);
        setMobileIndex((i) => Math.min(i, Math.max(0, data.photos.length - 1)));
      });
  };

  const handleDownload = (photo) => {
    const url = UPLOADS_URL + photo.filename;
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Mobile carousel swipe
  const mobileSwipeStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const mobileSwipeEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) setMobileIndex((i) => (i < photos.length - 1 ? i + 1 : i));
    else setMobileIndex((i) => (i > 0 ? i - 1 : i));
  };

  // Lightbox
  const lightboxPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null;
  const lbPrev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const lbNext = () => setLightboxIndex((i) => (i < photos.length - 1 ? i + 1 : 0));
  const lbSwipeStart = (e) => { lbTouchStartX.current = e.touches[0].clientX; };
  const lbSwipeEnd = (e) => {
    if (lbTouchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - lbTouchStartX.current;
    lbTouchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) lbNext();
    else lbPrev();
  };

  const emptyState = (
    <div style={{ textAlign: "center", padding: "60px 20px", maxWidth: "500px", margin: "0 auto" }}>
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--blue-300)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 16px" }}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
      <p style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "var(--blue-400)", fontStyle: "italic" }}>
        {t.galleryEmpty}
      </p>
    </div>
  );

  const spinner = (
    <div style={{ textAlign: "center", padding: "40px" }}>
      <div style={{
        width: "40px", height: "40px",
        border: "3px solid var(--blue-200)",
        borderTop: "3px solid var(--blue-600)",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        margin: "0 auto",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <section id="gallery" style={{ padding: "80px 20px", background: "white" }}>
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <p style={{ letterSpacing: "3px", textTransform: "uppercase", fontSize: "0.85rem", color: "var(--blue-400)", marginBottom: "8px" }}>
          {t.gallerySubtitle}
        </p>
        <h2 className="section-heading">{t.galleryHeading}</h2>
        <div className="divider-ornament">
          <span style={{ color: "var(--gold)" }}></span>
        </div>
        <p style={{ color: "#64748b", maxWidth: "500px", margin: "16px auto 0", fontSize: "1rem" }}>
          {t.galleryDescription}
        </p>
      </div>

      {/* Upload button */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} style={{ display: "none" }} id="gallery-upload" />
        <button
          className="btn-wedding"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ display: "inline-flex", alignItems: "center", gap: "8px", opacity: uploading ? 0.7 : 1 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {uploading ? t.galleryUploading : t.galleryUpload}
        </button>
      </div>

      {loading ? spinner : photos.length === 0 ? emptyState : isMobile ? (
        /* ── Mobile: single-photo swipe carousel ── */
        <div
          style={{ userSelect: "none", WebkitUserSelect: "none" }}
          onTouchStart={mobileSwipeStart}
          onTouchEnd={mobileSwipeEnd}
        >
          {/* Photo */}
          <div style={{
            maxWidth: "100%",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            aspectRatio: "1",
            background: "#f1f5f9",
            position: "relative",
          }}>
            <img
              src={UPLOADS_URL + photos[mobileIndex].filename}
              alt={photos[mobileIndex].filename}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>

          {/* Counter + arrows */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "20px", marginTop: "16px" }}>
            <button
              onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
              disabled={mobileIndex === 0}
              aria-label="Previous photo"
              style={{
                width: "40px", height: "40px", borderRadius: "50%",
                background: mobileIndex === 0 ? "#e2e8f0" : "var(--blue-600)",
                color: mobileIndex === 0 ? "#94a3b8" : "white",
                border: "none", fontSize: "1.4rem", cursor: mobileIndex === 0 ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >‹</button>

            <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", color: "#64748b" }}>
              {mobileIndex + 1} / {photos.length}
            </span>

            <button
              onClick={() => setMobileIndex((i) => Math.min(photos.length - 1, i + 1))}
              disabled={mobileIndex === photos.length - 1}
              aria-label="Next photo"
              style={{
                width: "40px", height: "40px", borderRadius: "50%",
                background: mobileIndex === photos.length - 1 ? "#e2e8f0" : "var(--blue-600)",
                color: mobileIndex === photos.length - 1 ? "#94a3b8" : "white",
                border: "none", fontSize: "1.4rem", cursor: mobileIndex === photos.length - 1 ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >›</button>
          </div>

          {/* Download / Delete for current mobile photo */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
            <button
              onClick={() => handleDownload(photos[mobileIndex])}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "10px 20px", background: "var(--blue-600)", color: "white",
                border: "none", borderRadius: "8px", cursor: "pointer",
                fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {t.galleryDownload}
            </button>
            <button
              onClick={() => handleDelete(photos[mobileIndex].filename)}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "10px 20px", background: "transparent", color: "#ef4444",
                border: "2px solid #ef4444", borderRadius: "8px", cursor: "pointer",
                fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              {t.galleryDelete}
            </button>
          </div>
        </div>
      ) : (
        /* ── Desktop: paginated grid ── */
        <>
          <div style={{
            maxWidth: "1000px", margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "16px",
          }}>
            {pagePhotos.map((photo, idx) => (
              <div
                key={photo.filename}
                style={{
                  position: "relative", borderRadius: "12px", overflow: "hidden",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)", cursor: "pointer",
                  aspectRatio: "1", background: "#f1f5f9",
                  transition: "transform 0.3s, box-shadow 0.3s",
                }}
                onClick={() => setLightboxIndex((currentPage - 1) * PHOTOS_PER_PAGE + idx)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
                }}
              >
                <img
                  src={UPLOADS_URL + photo.filename}
                  alt={photo.filename}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", marginTop: "40px" }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "8px 20px",
                  background: currentPage === 1 ? "#e2e8f0" : "var(--blue-600)",
                  color: currentPage === 1 ? "#94a3b8" : "white",
                  border: "none", borderRadius: "8px",
                  cursor: currentPage === 1 ? "default" : "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600,
                }}
              >{t.galleryPrev}</button>
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", color: "#64748b" }}>
                {t.galleryPage(currentPage, totalPages)}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "8px 20px",
                  background: currentPage === totalPages ? "#e2e8f0" : "var(--blue-600)",
                  color: currentPage === totalPages ? "#94a3b8" : "white",
                  border: "none", borderRadius: "8px",
                  cursor: currentPage === totalPages ? "default" : "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600,
                }}
              >{t.galleryNext}</button>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            style={{
              background: "white", borderRadius: "16px",
              padding: "32px", maxWidth: "380px", width: "100%",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: "16px" }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto" }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: "#1e293b", marginBottom: "8px" }}>
              Remove Photo?
            </h3>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.95rem", color: "#64748b", marginBottom: "28px" }}>
              This photo will be saved on the server in case you need it back. You can ask us to restore it at any time.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: "10px 24px", background: "#f1f5f9", color: "#475569",
                  border: "none", borderRadius: "8px", cursor: "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.95rem", fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePhoto}
                style={{
                  padding: "10px 24px", background: "#ef4444", color: "white",
                  border: "none", borderRadius: "8px", cursor: "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.95rem", fontWeight: 600,
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox (desktop only — on mobile actions are inline) */}
      {!isMobile && lightboxPhoto && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px", backdropFilter: "blur(4px)",
          }}
          onClick={() => setLightboxIndex(null)}
          onTouchStart={lbSwipeStart}
          onTouchEnd={lbSwipeEnd}
        >
          <div
            style={{
              position: "relative", maxWidth: "90vw", maxHeight: "90vh",
              display: "flex", flexDirection: "column", alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxIndex(null)}
              style={{ position: "absolute", top: "-40px", right: "0", background: "none", border: "none", color: "white", fontSize: "2rem", cursor: "pointer", lineHeight: 1 }}
              aria-label="Close"
            >×</button>

            {photos.length > 1 && (
              <button onClick={lbPrev} aria-label="Previous photo" style={{
                position: "absolute", left: "-48px", top: "50%", transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%",
                width: "40px", height: "40px", color: "white", fontSize: "1.4rem", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>‹</button>
            )}

            <img
              src={UPLOADS_URL + lightboxPhoto.filename}
              alt={lightboxPhoto.filename}
              style={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: "8px", objectFit: "contain", userSelect: "none", WebkitUserSelect: "none" }}
            />

            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", marginTop: "8px", fontFamily: "'Lato', sans-serif" }}>
              {lightboxIndex + 1} / {photos.length}
            </p>

            {photos.length > 1 && (
              <button onClick={lbNext} aria-label="Next photo" style={{
                position: "absolute", right: "-48px", top: "50%", transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%",
                width: "40px", height: "40px", color: "white", fontSize: "1.4rem", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>›</button>
            )}

            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
              <button
                onClick={() => handleDownload(lightboxPhoto)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "10px 24px", background: "var(--blue-600)", color: "white",
                  border: "none", borderRadius: "8px", cursor: "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600, transition: "background 0.3s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--blue-700)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--blue-600)")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t.galleryDownload}
              </button>
              <button
                onClick={() => handleDelete(lightboxPhoto.filename)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "10px 24px", background: "transparent", color: "#ef4444",
                  border: "2px solid #ef4444", borderRadius: "8px", cursor: "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 600, transition: "all 0.3s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.color = "white"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#ef4444"; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                {t.galleryDelete}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
